// Shared infrastructure for the Supabase adapter modules: the client, the
// current session identity, value/row normalization helpers, and image-column helpers.
// Domain-specific helpers live with their domain module.
import { supabase } from '../../../lib/supabaseClient';
import type { ImageFocus } from '@/lib/media/imageAssetFields'
export { supabase };

type InvokeBody =
  | string
  | File
  | Blob
  | ArrayBuffer
  | FormData
  | ReadableStream<Uint8Array>
  | object
  | undefined

// Value helpers — normalization at the DB/RPC row boundary, where payloads
// arrive untyped. Typed adapter inputs should not need these.
export type RecordLike = Record<string, unknown>

export function isRecord(value: unknown): value is RecordLike {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

export function toStringValue(value: unknown, fallback = ''): string {
  if (typeof value === 'string') return value
  if (value === null || value === undefined) return fallback
  return String(value)
}

export function toNullableString(value: unknown): string | null {
  const next = toStringValue(value).trim()
  return next ? next : null
}

export function toNumberValue(value: unknown, fallback = 0): number {
  const normalized = Number(value)
  return Number.isFinite(normalized) ? normalized : fallback
}

export function toNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : null
}

export function toUnknownList(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

export function toRecord(value: unknown): RecordLike {
  return isRecord(value) ? value : {}
}

export function toRecordArray(value: unknown): RecordLike[] {
  return Array.isArray(value) ? value.filter(isRecord) : []
}

export function normalizeStringList(values: unknown[] = []): string[] {
  return Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => value?.toString?.().trim?.() ?? '')
        .filter(Boolean),
    ),
  );
}

// Identity follows the SDK's current local session; it has no independent TTL.
// This is a query filter, not authorization. The backend authorizes every request.
export async function uid(): Promise<string> {
  const { data, error } = await supabase.auth.getSession()
  if (error) throw error
  if (!data.session?.user) throw new Error('[Supabase] Not authenticated')
  return data.session.user.id
}

// Image helpers
import {
  resolveStoredImageUrl as resolveStoredImageUrlBase,
  buildImageColumns as buildImageColumnsBase,
  buildImageFocusColumns as buildImageFocusColumnsBase,
  normalizeAppImage as normalizeAppImageBase,
  getStoredImageAsset as getStoredImageAssetBase,
  getStoredImageFocus as getStoredImageFocusBase,
  sameStoredImageAsset as sameStoredImageAssetBase,
} from '@/lib/media/imageAssetFields'
import { deletePublicImage, getSupabasePublicUrl } from '@/lib/media/supabaseStorageProvider'

export function resolveStoredImageUrl(options?: {
  row?: unknown
  prefix?: string
  fallbackUrl?: string
  getPublicUrl?: (bucket: string, path: string) => string
}): string {
  return resolveStoredImageUrlBase(options as Parameters<typeof resolveStoredImageUrlBase>[0])
}

export function buildImageColumns(prefix: string, assetInput: unknown): Record<string, unknown> {
  return buildImageColumnsBase(prefix, assetInput)
}

export function buildImageFocusColumns(prefix: string, focusInput: unknown): Record<string, unknown> {
  return buildImageFocusColumnsBase(prefix, focusInput)
}

export function normalizeAppImage(value: unknown, fallback?: Parameters<typeof normalizeAppImageBase>[1]) {
  return normalizeAppImageBase(value, fallback)
}

export function getStoredImageAsset(row: unknown, prefix: string) {
  return getStoredImageAssetBase(row as Parameters<typeof getStoredImageAssetBase>[0], prefix)
}

export function getStoredImageFocus(row: unknown, prefix: string): ImageFocus {
  return getStoredImageFocusBase(row as Parameters<typeof getStoredImageFocusBase>[0], prefix)
}

export function sameStoredImageAsset(a: unknown, b: unknown): boolean {
  return sameStoredImageAssetBase(a, b)
}

// Stored-image column families: `avatar_*` (profiles, worlds), `img_*` (characters, personas).
export const AVATAR_IMAGE_SELECT = [
  'avatar_url', 'avatar_provider', 'avatar_bucket', 'avatar_storage_path',
  'avatar_mime_type', 'avatar_bytes', 'avatar_width', 'avatar_height', 'avatar_original_name',
  'avatar_focus_x', 'avatar_focus_y',
].join(', ')

export const ENTITY_IMAGE_SELECT = [
  'imgpath', 'img_provider', 'img_bucket', 'img_storage_path',
  'img_mime_type', 'img_bytes', 'img_width', 'img_height', 'img_original_name',
  'img_focus_x', 'img_focus_y',
].join(', ')

export function getPublicImageUrl(bucket: string, path: string) {
  return getSupabasePublicUrl(bucket, path)
}

export async function safeDeleteStoredImage(asset: unknown) {
  try {
    await deletePublicImage(asset && typeof asset === 'object' ? (asset as { bucket?: string; path?: string }) : null)
  } catch (error: unknown) {
    const storedAsset = asset && typeof asset === 'object' ? (asset as Record<string, unknown>) : null
    console.warn('[SupabaseAdapter] Failed to delete stored image', {
      bucket: storedAsset?.bucket,
      path: storedAsset?.path,
      error: error instanceof Error ? error.message : String(error),
    })
  }
}

export function buildEntityImageColumns(asset: unknown, focusInput?: unknown) {
  const columns = buildImageColumns('img', asset)
  const payload = {
    imgpath: columns.imgpath,
    img_provider: columns.img_provider,
    img_bucket: columns.img_bucket,
    img_storage_path: columns.img_storage_path,
    img_mime_type: columns.img_mime_type,
    img_bytes: columns.img_bytes,
    img_width: columns.img_width,
    img_height: columns.img_height,
    img_original_name: columns.img_original_name,
  }
  if (!columns.img_bucket && !columns.img_storage_path) {
    Object.assign(payload, buildImageFocusColumns('img', null))
  } else if (focusInput !== undefined) {
    Object.assign(payload, buildImageFocusColumns('img', focusInput))
  }
  return payload
}

export function buildEntityImageFocusColumns(focusInput: unknown) {
  return buildImageFocusColumns('img', focusInput)
}

export function buildEntityImageColumnsFromInput(input: {
  imgpath?: unknown
  img_asset?: unknown
  img_focus?: unknown
  imageAssetProvided?: boolean
}) {
  if (input.imageAssetProvided) {
    const asset = normalizeAppImage(input.img_asset)?.asset
    if (asset) return buildEntityImageColumns(asset, input.img_focus)
    const fallbackImage = normalizeAppImage(null, {
      url: typeof input.imgpath === 'string' ? input.imgpath : '',
      focus: input.img_focus as ImageFocus | null | undefined,
      asset: input.img_asset ?? null,
    })
    if (input.img_asset === null && !fallbackImage?.url) return buildEntityImageColumns(null, null)
    return input.img_focus !== undefined ? buildEntityImageFocusColumns(input.img_focus) : {}
  }

  return input.img_focus !== undefined ? buildEntityImageFocusColumns(input.img_focus) : {}
}

export async function invokeFunction(functionName: string, body: InvokeBody) {
  const { data, error } = await supabase.functions.invoke(functionName, { body });
  if (error) throw error;
  return data;
}
